import React, { useState, useEffect } from "react";
import axios from "axios";
import { HostingClientsPanel } from "../components/admin/HostingClientsPanel";
import { NotesPanel } from "../components/admin/NotesPanel";
import { Lock, Users, Zap, TrendingUp, LogOut, Send, Check, AlertCircle, Trash2, X, QrCode, ScanLine, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ggm_stats_password";
const RANGE_KEY = "ggm_stats_range";

const RANGES = [
  { key: "all", label: "All time" },
  { key: "24h", label: "24h" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
];

const rangeLabel = (key) => RANGES.find((r) => r.key === key)?.label || key;

const NAVY = "#1a2744";
const NAVY_DEEP = "#131c33";
const GOLD = "#f5a623";
const GOLD_HOVER = "#ffb638";

const StatCard = ({ label, value, Icon, sub }) => (
  <div
    style={{
      background: NAVY_DEEP,
      border: "1px solid rgba(245,166,35,0.25)",
      borderRadius: 14,
      padding: 20,
      flex: "1 1 200px",
    }}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 12,
        fontFamily: '"Roboto Condensed", sans-serif',
        textTransform: "uppercase",
        letterSpacing: "0.18em",
        fontSize: 11,
        color: GOLD,
      }}
    >
      <Icon size={14} />
      {label}
    </div>
    <div
      style={{
        fontFamily: '"Roboto Condensed", sans-serif',
        fontWeight: 700,
        fontSize: 42,
        lineHeight: 1,
        color: "#ffffff",
        letterSpacing: "-0.02em",
      }}
    >
      {value}
    </div>
    {sub && (
      <div
        style={{
          marginTop: 8,
          fontSize: 13,
          color: "rgba(255,255,255,0.55)",
        }}
      >
        {sub}
      </div>
    )}
  </div>
);

const DailyScanChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, margin: 0 }}>
        No scans yet in this range.
      </p>
    );
  }
  const max = Math.max(1, ...data.map((d) => d.count));
  const showEveryNth = data.length > 14 ? Math.ceil(data.length / 10) : 1;
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: data.length > 20 ? 3 : 6,
          height: 140,
          padding: "8px 0",
        }}
      >
        {data.map((d, idx) => {
          const heightPct = (d.count / max) * 100;
          const day = new Date(d.date + "T00:00:00Z");
          const label = day.toLocaleDateString([], { month: "short", day: "numeric", timeZone: "UTC" });
          return (
            <div
              key={d.date}
              data-testid={`daily-scan-bar-${d.date}`}
              title={`${label}: ${d.count} scan${d.count === 1 ? "" : "s"}`}
              style={{
                flex: "1 1 0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 6,
                minWidth: 0,
                height: "100%",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: d.count > 0 ? "#ffffff" : "rgba(255,255,255,0.25)",
                  fontFamily: '"Roboto Condensed", sans-serif',
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                }}
              >
                {d.count > 0 ? d.count : ""}
              </div>
              <div
                style={{
                  width: "100%",
                  height: `${Math.max(heightPct, d.count > 0 ? 4 : 2)}%`,
                  background:
                    d.count > 0
                      ? `linear-gradient(180deg, ${GOLD_HOVER}, ${GOLD})`
                      : "rgba(255,255,255,0.06)",
                  borderRadius: 4,
                  transition: "height 320ms ease",
                }}
              />
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          gap: data.length > 20 ? 3 : 6,
          marginTop: 8,
        }}
      >
        {data.map((d, idx) => {
          const day = new Date(d.date + "T00:00:00Z");
          const showLabel = idx % showEveryNth === 0 || idx === data.length - 1;
          const label = day.toLocaleDateString([], { month: "short", day: "numeric", timeZone: "UTC" });
          return (
            <div
              key={`lbl-${d.date}`}
              style={{
                flex: "1 1 0",
                minWidth: 0,
                textAlign: "center",
                fontSize: 10,
                color: "rgba(255,255,255,0.45)",
                fontFamily: '"Roboto Condensed", sans-serif',
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {showLabel ? label : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const shortDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const shortAgent = (ua) => {
  if (!ua) return "—";
  if (/iphone|ipad/i.test(ua)) return "iOS";
  if (/android/i.test(ua)) return "Android";
  if (/mac os/i.test(ua)) return "macOS";
  if (/windows/i.test(ua)) return "Windows";
  if (/linux/i.test(ua)) return "Linux";
  return ua.slice(0, 30);
};

export default function RobertStatsPage() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [digestState, setDigestState] = useState("idle"); // idle | sending | success | error
  const [digestMsg, setDigestMsg] = useState("");
  const [range, setRange] = useState(() => localStorage.getItem(RANGE_KEY) || "all");
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadStats = async (pw, rng = range) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/track/robert-scan/stats`, {
        params: { range: rng },
        headers: { "x-stats-password": pw },
      });
      setStats(data);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, pw);
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("Wrong password. Try again.");
        localStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
      } else {
        setError("Couldn't load stats. Try again in a sec.");
      }
    } finally {
      setLoading(false);
    }
  };

  const changeRange = (rng) => {
    setRange(rng);
    localStorage.setItem(RANGE_KEY, rng);
    const pw = localStorage.getItem(STORAGE_KEY) || password;
    if (pw && authed) loadStats(pw, rng);
  };

  const sendDigestNow = async () => {
    const pw = localStorage.getItem(STORAGE_KEY) || password;
    if (!pw) return;
    setDigestState("sending");
    setDigestMsg("");
    try {
      const { data } = await axios.post(
        `${API}/track/robert-scan/digest/send-now`,
        {},
        {
          params: { range },
          headers: { "x-stats-password": pw },
        }
      );
      if (data?.ok) {
        setDigestState("success");
        setDigestMsg(`Digest for ${rangeLabel(range)} sent to your inbox.`);
      } else {
        setDigestState("error");
        setDigestMsg("Send failed — check SendGrid config.");
      }
    } catch (err) {
      setDigestState("error");
      setDigestMsg(
        err?.response?.status === 401
          ? "Session expired. Log in again."
          : "Couldn't send digest. Try again."
      );
    } finally {
      setTimeout(() => {
        setDigestState("idle");
        setDigestMsg("");
      }, 4000);
    }
  };

  const clearStats = async () => {
    const pw = localStorage.getItem(STORAGE_KEY) || password;
    if (!pw) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/track/robert-scan/clear`, {
        headers: { "x-stats-password": pw },
      });
      setConfirmClear(false);
      await loadStats(pw, range);
    } catch {
      setDigestState("error");
      setDigestMsg("Couldn't clear stats. Try again.");
      setTimeout(() => {
        setDigestState("idle");
        setDigestMsg("");
      }, 4000);
    } finally {
      setClearing(false);
    }
  };


  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setPassword(stored);
      loadStats(stored);
    }
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    loadStats(password.trim());
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setStats(null);
    setPassword("");
  };

  const pageStyle = {
    minHeight: "100vh",
    background: NAVY,
    color: "#ffffff",
    fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
    padding: "48px 24px",
  };

  // ─── Password gate ───
  if (!authed) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid="stats-lock"
      >
        <form
          onSubmit={submit}
          style={{
            width: "100%",
            maxWidth: 380,
            background: NAVY_DEEP,
            border: "1px solid rgba(245,166,35,0.25)",
            borderRadius: 20,
            padding: 32,
            textAlign: "center",
            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.55)",
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              margin: "0 auto 20px",
              background: "rgba(245,166,35,0.14)",
              border: `1px solid ${GOLD}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Lock size={22} color={GOLD} />
          </div>
          <h1
            style={{
              fontFamily: '"Roboto Condensed", sans-serif',
              fontWeight: 700,
              fontSize: 24,
              margin: 0,
              color: "#ffffff",
            }}
          >
            /robert Stats
          </h1>
          <p
            style={{
              margin: "10px 0 24px 0",
              color: "rgba(255,255,255,0.55)",
              fontSize: 14,
            }}
          >
            Enter the password to unlock scan analytics.
          </p>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="stats-password-input"
            autoFocus
            style={{
              padding: "14px 16px",
              width: "100%",
              boxSizing: "border-box",
              background: NAVY,
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 10,
              color: "#ffffff",
              fontSize: 15,
              outline: "none",
              transition: "border-color 180ms, box-shadow 180ms",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = GOLD;
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(245,166,35,0.22)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)";
              e.currentTarget.style.boxShadow = "none";
            }}
          />
          {error && (
            <p
              style={{ color: "#ff8a8a", fontSize: 13, margin: "10px 0 0 0" }}
              data-testid="stats-error"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="stats-unlock-btn"
            style={{
              marginTop: 16,
              padding: "14px 22px",
              width: "100%",
              background: GOLD,
              color: NAVY,
              border: "none",
              borderRadius: 10,
              fontFamily: '"Roboto Condensed", sans-serif',
              fontWeight: 700,
              fontSize: 15,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              transition: "background 180ms",
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.background = GOLD_HOVER;
            }}
            onMouseLeave={(e) => (e.currentTarget.style.background = GOLD)}
          >
            {loading ? "Unlocking…" : "Unlock"}
          </button>
        </form>
      </div>
    );
  }

  // ─── Dashboard ───
  const s = stats || { total_scans: 0, total_leads: 0, conversion_pct: 0, recent_scans: [], recent_leads: [], top_sources: [] };

  return (
    <div style={pageStyle} data-testid="stats-dashboard">
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 32,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: '"Roboto Condensed", sans-serif',
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.24em",
                fontSize: 11,
                color: GOLD,
              }}
            >
              Analytics Dashboard
            </div>
            <h1
              style={{
                margin: "8px 0 0 0",
                fontFamily: '"Roboto Condensed", sans-serif',
                fontWeight: 700,
                fontSize: 32,
                color: "#ffffff",
                letterSpacing: "-0.01em",
              }}
            >
              /robert Card Performance
            </h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={sendDigestNow}
              disabled={digestState === "sending"}
              data-testid="stats-send-digest-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background:
                  digestState === "success"
                    ? "rgba(76,175,80,0.15)"
                    : digestState === "error"
                    ? "rgba(255,138,138,0.12)"
                    : GOLD,
                color:
                  digestState === "success"
                    ? "#8bd48f"
                    : digestState === "error"
                    ? "#ff8a8a"
                    : NAVY,
                border:
                  digestState === "success"
                    ? "1px solid rgba(76,175,80,0.4)"
                    : digestState === "error"
                    ? "1px solid rgba(255,138,138,0.4)"
                    : "none",
                borderRadius: 999,
                padding: "10px 18px",
                fontFamily: '"Roboto Condensed", sans-serif',
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontSize: 12,
                cursor: digestState === "sending" ? "not-allowed" : "pointer",
                opacity: digestState === "sending" ? 0.7 : 1,
                transition: "background 180ms, color 180ms, border-color 180ms",
              }}
              onMouseEnter={(e) => {
                if (digestState === "idle") e.currentTarget.style.background = GOLD_HOVER;
              }}
              onMouseLeave={(e) => {
                if (digestState === "idle") e.currentTarget.style.background = GOLD;
              }}
            >
              {digestState === "success" ? (
                <Check size={14} />
              ) : digestState === "error" ? (
                <AlertCircle size={14} />
              ) : (
                <Send size={14} />
              )}
              {digestState === "sending"
                ? "Sending…"
                : digestState === "success"
                ? "Sent"
                : digestState === "error"
                ? "Failed"
                : `Send digest (${rangeLabel(range)})`}
            </button>
            <button
              onClick={() => setConfirmClear(true)}
              data-testid="stats-clear-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "transparent",
                color: "#ff8a8a",
                border: "1px solid rgba(255,138,138,0.35)",
                borderRadius: 999,
                padding: "10px 18px",
                fontFamily: '"Roboto Condensed", sans-serif',
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontSize: 12,
                cursor: "pointer",
                transition: "background 180ms, border-color 180ms",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,138,138,0.08)";
                e.currentTarget.style.borderColor = "rgba(255,138,138,0.6)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "rgba(255,138,138,0.35)";
              }}
            >
              <Trash2 size={14} />
              Clear stats
            </button>
            <button
              onClick={logout}
              data-testid="stats-logout-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "transparent",
                color: "rgba(255,255,255,0.6)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 999,
                padding: "10px 18px",
                fontFamily: '"Roboto Condensed", sans-serif',
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontSize: 12,
                cursor: "pointer",
                transition: "color 180ms, border-color 180ms",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#ffffff";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
              }}
            >
              <LogOut size={14} />
              Log out
            </button>
          </div>
        </header>

        {/* Range chips */}
        <div
          data-testid="stats-range-selector"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 20,
            padding: 6,
            background: NAVY_DEEP,
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 999,
            width: "fit-content",
          }}
        >
          {RANGES.map((r) => {
            const active = range === r.key;
            return (
              <button
                key={r.key}
                onClick={() => changeRange(r.key)}
                data-testid={`stats-range-${r.key}`}
                style={{
                  padding: "8px 16px",
                  borderRadius: 999,
                  border: "none",
                  background: active ? GOLD : "transparent",
                  color: active ? NAVY : "rgba(255,255,255,0.6)",
                  fontFamily: '"Roboto Condensed", sans-serif',
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                  transition: "background 180ms, color 180ms",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.color = "#ffffff";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>

        {digestMsg && (
          <div
            data-testid="stats-digest-msg"
            style={{
              marginBottom: 20,
              padding: "12px 16px",
              borderRadius: 10,
              fontSize: 13,
              background:
                digestState === "success"
                  ? "rgba(76,175,80,0.1)"
                  : "rgba(255,138,138,0.08)",
              border:
                digestState === "success"
                  ? "1px solid rgba(76,175,80,0.3)"
                  : "1px solid rgba(255,138,138,0.3)",
              color: digestState === "success" ? "#8bd48f" : "#ff8a8a",
            }}
          >
            {digestMsg}
          </div>
        )}

        {/* Stat cards */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 32,
          }}
        >
          <StatCard label="Total Scans" value={s.total_scans} Icon={Zap} sub="Card page loads (incl. QR scans)" />
          <StatCard label="Leads Captured" value={s.total_leads} Icon={Users} sub="Form submits" />
          <StatCard
            label="Conversion"
            value={`${s.conversion_pct}%`}
            Icon={TrendingUp}
            sub="Leads ÷ scans"
          />
          <StatCard
            label="Contact QR Taps"
            value={s.contact_qr_taps ?? 0}
            Icon={QrCode}
            sub="QR beside contact form on gadgetguymedia.com"
          />
          <StatCard
            label="Main QR Taps"
            value={s.main_qr_taps ?? 0}
            Icon={ScanLine}
            sub="Big QR near bottom of /robert card"
          />
          <StatCard
            label="Audit Page Visits"
            value={s.audit_visits ?? 0}
            Icon={TrendingUp}
            sub="All loads of /audit (incl. QR scans)"
          />
          <StatCard
            label="Audit Print-QR Scans"
            value={s.audit_qr_scans ?? 0}
            Icon={QrCode}
            sub="Flyer / counter card QR → /audit"
          />
        </div>

        {/* Daily scan chart */}
        <section
          data-testid="stats-daily-chart"
          style={{
            background: NAVY_DEEP,
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 14,
            padding: 20,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 20,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontFamily: '"Roboto Condensed", sans-serif',
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                fontSize: 11,
                color: GOLD,
              }}
            >
              <BarChart3 size={14} />
              Scans by Day
            </div>
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.4)",
                fontFamily: '"Roboto Condensed", sans-serif',
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {rangeLabel(range)}
            </div>
          </div>
          <DailyScanChart data={s.daily_scans || []} />
        </section>

        <div style={{ height: 16 }} />

        {/* Top sources panel */}
        <section
          data-testid="stats-top-sources"
          style={{
            background: NAVY_DEEP,
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 14,
            padding: 20,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontFamily: '"Roboto Condensed", sans-serif',
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                fontSize: 11,
                color: GOLD,
              }}
            >
              Top Sources
            </div>
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.4)",
                fontFamily: '"Roboto Condensed", sans-serif',
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {rangeLabel(range)}
            </div>
          </div>
          {(!s.top_sources || s.top_sources.length === 0) ? (
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, margin: 0 }}>
              No scans yet in this range.
            </p>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
              {s.top_sources.map((src) => (
                <li
                  key={src.label}
                  data-testid={`stats-source-${src.label.toLowerCase().replace(/\s+/g, "-")}`}
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                    <span style={{ color: "#ffffff", fontWeight: 600 }}>{src.label}</span>
                    <span style={{ color: "rgba(255,255,255,0.55)" }}>
                      {src.count} <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 12 }}>· {src.pct}%</span>
                    </span>
                  </div>
                  <div
                    style={{
                      height: 6,
                      background: "rgba(255,255,255,0.06)",
                      borderRadius: 999,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(src.pct, 2)}%`,
                        height: "100%",
                        background: `linear-gradient(90deg, ${GOLD}, ${GOLD_HOVER})`,
                        borderRadius: 999,
                        transition: "width 320ms ease",
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Two-column recent activity */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          <section
            style={{
              background: NAVY_DEEP,
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              padding: 20,
            }}
          >
            <div
              style={{
                fontFamily: '"Roboto Condensed", sans-serif',
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                fontSize: 11,
                color: GOLD,
                marginBottom: 16,
              }}
            >
              Recent Leads
            </div>
            {s.recent_leads?.length === 0 ? (
              <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, margin: 0 }}>
                No leads yet.
              </p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {s.recent_leads?.map((l) => (
                  <li
                    key={l.id}
                    style={{
                      padding: "10px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      fontSize: 14,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "#ffffff", fontWeight: 600 }}>
                        {l.first_name}
                      </div>
                      <div
                        style={{
                          color: "rgba(255,255,255,0.5)",
                          fontSize: 12,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {l.email}
                      </div>
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, whiteSpace: "nowrap" }}>
                      {shortDate(l.created_at)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section
            style={{
              background: NAVY_DEEP,
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              padding: 20,
            }}
          >
            <div
              style={{
                fontFamily: '"Roboto Condensed", sans-serif',
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                fontSize: 11,
                color: GOLD,
                marginBottom: 16,
              }}
            >
              Recent Scans
            </div>
            {s.recent_scans?.length === 0 ? (
              <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, margin: 0 }}>
                No scans yet.
              </p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {s.recent_scans?.map((sc) => (
                  <li
                    key={sc.id}
                    style={{
                      padding: "10px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      fontSize: 14,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "#ffffff" }}>{shortAgent(sc.user_agent)}</div>
                      <div
                        style={{
                          color: "rgba(255,255,255,0.5)",
                          fontSize: 12,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {sc.referrer || "direct"}
                      </div>
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, whiteSpace: "nowrap" }}>
                      {shortDate(sc.created_at)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <NotesPanel password={localStorage.getItem(STORAGE_KEY) || password} />
        <HostingClientsPanel password={localStorage.getItem(STORAGE_KEY) || password} />

        <p
          style={{
            marginTop: 32,
            textAlign: "center",
            fontSize: 11,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.35)",
            fontFamily: '"Roboto Condensed", sans-serif',
          }}
        >
          gadgetguymedia.com/robert/stats
        </p>
      </div>

      {/* Clear confirmation modal */}
      {confirmClear && (
        <div
          data-testid="stats-clear-modal"
          onClick={() => !clearing && setConfirmClear(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(9,12,24,0.72)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              background: NAVY_DEEP,
              border: "1px solid rgba(255,138,138,0.25)",
              borderRadius: 16,
              padding: 28,
              boxShadow: "0 40px 100px -30px rgba(0,0,0,0.7)",
              position: "relative",
            }}
          >
            <button
              onClick={() => !clearing && setConfirmClear(false)}
              aria-label="Close"
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "transparent",
                border: "none",
                color: "rgba(255,255,255,0.5)",
                cursor: clearing ? "not-allowed" : "pointer",
                padding: 6,
                display: "flex",
              }}
            >
              <X size={18} />
            </button>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background: "rgba(255,138,138,0.14)",
                border: "1px solid rgba(255,138,138,0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <Trash2 size={20} color="#ff8a8a" />
            </div>
            <h2
              style={{
                margin: 0,
                fontFamily: '"Roboto Condensed", sans-serif',
                fontWeight: 700,
                fontSize: 22,
                color: "#ffffff",
              }}
            >
              Clear all stats?
            </h2>
            <p
              style={{
                margin: "10px 0 24px 0",
                color: "rgba(255,255,255,0.6)",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              This permanently deletes <strong style={{ color: "#ffffff" }}>every scan and lead</strong> from the database. There&apos;s no undo.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                onClick={() => setConfirmClear(false)}
                disabled={clearing}
                data-testid="stats-clear-cancel"
                style={{
                  padding: "12px 20px",
                  background: "transparent",
                  color: "rgba(255,255,255,0.7)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 10,
                  fontFamily: '"Roboto Condensed", sans-serif',
                  fontWeight: 700,
                  fontSize: 13,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  cursor: clearing ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={clearStats}
                disabled={clearing}
                data-testid="stats-clear-confirm"
                style={{
                  padding: "12px 20px",
                  background: "#ff8a8a",
                  color: NAVY,
                  border: "none",
                  borderRadius: 10,
                  fontFamily: '"Roboto Condensed", sans-serif',
                  fontWeight: 700,
                  fontSize: 13,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  cursor: clearing ? "not-allowed" : "pointer",
                  opacity: clearing ? 0.7 : 1,
                }}
              >
                {clearing ? "Clearing…" : "Yes, wipe it all"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
